// REUNIÃO GERAL (2026-09-03) — a pauta da sexta, aberta.
//
// A pauta e a ata são A MESMA TELA de propósito: o que o robô escreveu na
// segunda é o rascunho em cima do qual a ata é digitada ao vivo na sexta. Ter
// "documento da reunião" separado de "registro do que foi decidido" é
// exatamente o que fez a Weekly do gestor acumular 6 briefings e nenhuma ata.
//
// Quem só tem `.ver` cai aqui em modo leitura: lê a pauta publicada, baixa o
// material e vê os combinados. Rascunho não chega até essa pessoa — o filtro
// está no SQL do backend, não neste componente.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  App as AntApp, Button, Collapse, DatePicker, Descriptions, Input, Space, Spin, Tag, Typography,
} from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, EditOutlined, EyeInvisibleOutlined,
  ReloadOutlined, SaveOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import RichTextEditor from '../RichTextEditor';
import { usePerms } from '../../hooks/usePerms';
import MaterialPanel from './MaterialPanel';
import CombinadosPanel from './CombinadosPanel';
import EscalaPanel from './EscalaPanel';
import { api, erroMsg, fmtData, fmtDataHora, fmtSemana, isoDia, nomeDoEmail, num } from './utils';
import { Anexo, Combinado, Pauta, PERM_GERENCIAR, STATUS_REUNIAO } from './types';

const { Text, Title, Paragraph } = Typography;

interface Props {
  id: string;
  onVoltar: () => void;
  onMudouLista: () => void;
}

export default function PautaDetalhe({ id, onVoltar, onMudouLista }: Props) {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();

  const [pauta, setPauta] = useState<Pauta | null>(null);
  const [combinados, setCombinados] = useState<Combinado[]>([]);
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [loading, setLoading] = useState(true);

  // Ata: editor local + dirty. O ref espelha o dirty para o carregar() não
  // sobrescrever o que está sendo digitado quando outro campo é salvo.
  const [ataHtml, setAtaHtml] = useState('');
  const [ataDirty, setAtaDirty] = useState(false);
  const ataDirtyRef = useRef(false);
  const [salvandoAta, setSalvandoAta] = useState(false);

  // Pauta: mesma mecânica, para quem quer ajustar o texto do robô.
  const [pautaHtml, setPautaHtml] = useState('');
  const [pautaDirty, setPautaDirty] = useState(false);
  const pautaDirtyRef = useRef(false);
  const [editandoPauta, setEditandoPauta] = useState(false);
  const [salvandoPauta, setSalvandoPauta] = useState(false);

  const [editandoCabecalho, setEditandoCabecalho] = useState(false);
  const [tituloEdit, setTituloEdit] = useState('');
  const [dataEdit, setDataEdit] = useState<any>(null);
  const [temaEdit, setTemaEdit] = useState('');

  const [gerando, setGerando] = useState(false);
  const [publicando, setPublicando] = useState(false);

  const podeGerenciar = pode(PERM_GERENCIAR);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('op_pauta_obter', { id });
      const p: Pauta | null = d?.reuniao || null;
      setPauta(p);
      setCombinados(Array.isArray(d?.encaminhamentos) ? d.encaminhamentos : []);
      setAnexos(Array.isArray(d?.anexos) ? d.anexos : []);
      if (!ataDirtyRef.current) { setAtaHtml(p?.ata_html || ''); setAtaDirty(false); }
      if (!pautaDirtyRef.current) { setPautaHtml(p?.pauta_html || ''); setPautaDirty(false); }
    } catch (e: any) {
      message.error('Falha ao carregar a pauta: ' + erroMsg(e));
    } finally { setLoading(false); }
  }, [id, message]);
  useEffect(() => { carregar(); }, [carregar]);

  async function salvarAta() {
    setSalvandoAta(true);
    try {
      await api('op_pauta_atualizar', { id, ata_html: ataHtml });
      ataDirtyRef.current = false; setAtaDirty(false);
      message.success('Ata salva.');
      onMudouLista();
    } catch (e: any) {
      message.error('Falha ao salvar a ata: ' + erroMsg(e));
    } finally { setSalvandoAta(false); }
  }

  async function salvarPauta() {
    setSalvandoPauta(true);
    try {
      await api('op_pauta_atualizar', { id, pauta_html: pautaHtml });
      pautaDirtyRef.current = false; setPautaDirty(false);
      setEditandoPauta(false);
      message.success('Pauta salva.');
    } catch (e: any) {
      message.error('Falha ao salvar a pauta: ' + erroMsg(e));
    } finally { setSalvandoPauta(false); }
  }

  async function salvarCabecalho() {
    try {
      await api('op_pauta_atualizar', {
        id,
        titulo: tituloEdit.trim() || undefined,
        data_reuniao: dataEdit ? dataEdit.format('YYYY-MM-DD') : undefined,
        tema_tecnico: temaEdit.trim(),
      });
      setEditandoCabecalho(false);
      await carregar();
      onMudouLista();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + erroMsg(e));
    }
  }

  function remontar() {
    modal.confirm({
      title: 'Montar a pauta de novo?',
      content: pautaDirty || (pauta?.pauta_html && pauta.pauta_html !== pautaHtml)
        ? 'O texto atual da pauta será SUBSTITUÍDO pelo que o robô montar agora, com os dados de hoje. O que você editou à mão se perde.'
        : 'O robô vai reler os dados do sistema e reescrever os blocos da pauta.',
      okText: 'Montar de novo',
      cancelText: 'Cancelar',
      onOk: async () => {
        setGerando(true);
        try {
          // A IA entra na montagem: o teto do API Gateway são 29 s, então o
          // timeout do axios aqui é folga para a resposta, não promessa.
          await api('op_pauta_gerar', { id }, 120000);
          pautaDirtyRef.current = false;
          message.success('Pauta remontada.');
          await carregar();
        } catch (e: any) {
          message.error('Falha ao montar: ' + erroMsg(e));
        } finally { setGerando(false); }
      },
    });
  }

  function publicar() {
    const semTema = !pauta?.tema_tecnico;
    modal.confirm({
      title: 'Publicar a pauta para o escritório?',
      width: '94vw',
      style: { maxWidth: 620, top: 40 },
      content: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>Todo mundo passa a ver a pauta e o material, e recebe aviso no sininho.</Text>
          {semTema && (
            <Text type="warning">
              O tema técnico está em branco — vale definir quem apresenta antes de publicar.
            </Text>
          )}
        </Space>
      ),
      okText: 'Publicar',
      cancelText: 'Ainda não',
      onOk: async () => {
        setPublicando(true);
        try {
          const d = await api('op_pauta_publicar', { id }, 60000);
          message.success(`Pauta publicada — ${num(d?.avisados)} pessoa(s) avisada(s).`);
          await carregar();
          onMudouLista();
        } catch (e: any) {
          message.error('Falha ao publicar: ' + erroMsg(e));
        } finally { setPublicando(false); }
      },
    });
  }

  async function despublicar() {
    setPublicando(true);
    try {
      await api('op_pauta_despublicar', { id });
      message.success('Pauta voltou a rascunho — o time não vê mais.');
      await carregar();
      onMudouLista();
    } catch (e: any) {
      message.error('Falha: ' + erroMsg(e));
    } finally { setPublicando(false); }
  }

  if (loading && !pauta) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
  }
  if (!pauta) {
    return (
      <Space direction="vertical" style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onVoltar}>Voltar</Button>
        <Text type="secondary">Pauta não encontrada.</Text>
      </Space>
    );
  }

  const publicada = !!pauta.publicada_em;
  const st = STATUS_REUNIAO[pauta.status] || { label: pauta.status, cor: 'default' };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* ── Cabeçalho ─────────────────────────────────────────────────────── */}
      <Space wrap style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <Space direction="vertical" size={2}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={onVoltar}
            style={{ paddingLeft: 0 }}>
            Todas as reuniões
          </Button>
          {editandoCabecalho ? (
            <Space wrap>
              <Input value={tituloEdit} onChange={(e) => setTituloEdit(e.target.value)}
                style={{ width: 320 }} placeholder="título" />
              <DatePicker value={dataEdit} onChange={setDataEdit} format="DD/MM/YYYY" />
              <Input value={temaEdit} onChange={(e) => setTemaEdit(e.target.value)}
                style={{ width: 280 }} placeholder="tema técnico" />
              <Button type="primary" size="small" onClick={salvarCabecalho}>Salvar</Button>
              <Button size="small" onClick={() => setEditandoCabecalho(false)}>Cancelar</Button>
            </Space>
          ) : (
            <Space align="baseline" wrap>
              <Title level={4} style={{ margin: 0 }}>{pauta.titulo}</Title>
              <Tag color={st.cor}>{st.label}</Tag>
              {publicada
                ? <Tag color="green">publicada</Tag>
                : <Tag color="orange">rascunho — só você vê</Tag>}
              {podeGerenciar && (
                <Button type="text" size="small" icon={<EditOutlined />}
                  onClick={() => {
                    setTituloEdit(pauta.titulo);
                    setDataEdit(pauta.data_reuniao ? dayjs(isoDia(pauta.data_reuniao)) : null);
                    setTemaEdit(pauta.tema_tecnico || '');
                    setEditandoCabecalho(true);
                  }} />
              )}
            </Space>
          )}
        </Space>

        {podeGerenciar && (
          <Space wrap>
            <Button icon={<ReloadOutlined />} loading={gerando} onClick={remontar}>
              Montar de novo
            </Button>
            {publicada
              ? <Button icon={<EyeInvisibleOutlined />} loading={publicando} onClick={despublicar}>
                  Voltar a rascunho
                </Button>
              : <Button type="primary" icon={<CheckCircleOutlined />} loading={publicando}
                  onClick={publicar}>
                  Publicar para o escritório
                </Button>}
          </Space>
        )}
      </Space>

      <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }} bordered
        items={[
          { key: 'd', label: 'Reunião', children: `${fmtData(pauta.data_reuniao)} · 14h` },
          { key: 's', label: 'Semana', children: fmtSemana(pauta.semana_ref) },
          {
            key: 't', label: 'Tema técnico',
            children: pauta.tema_tecnico || <Text type="secondary">a definir</Text>,
          },
          {
            key: 'p', label: 'Publicada',
            children: publicada
              ? `${fmtDataHora(pauta.publicada_em)}${pauta.publicada_por ? ' · ' + nomeDoEmail(pauta.publicada_por) : ''}`
              : <Text type="warning">ainda não</Text>,
          },
        ]} />

      {!publicada && podeGerenciar && (
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Esta pauta é um <Text strong>rascunho</Text>: o robô montou na segunda de manhã e mais
          ninguém a vê. Ajuste o que precisar, defina quem apresenta e clique em{' '}
          <Text strong>Publicar</Text>.
        </Paragraph>
      )}

      {/* ── Blocos ────────────────────────────────────────────────────────── */}
      <Collapse
        defaultActiveKey={['pauta', 'combinados']}
        items={[
          {
            key: 'pauta',
            label: <Text strong>Pauta da reunião</Text>,
            extra: podeGerenciar && !editandoPauta
              ? <Button size="small" type="text" icon={<EditOutlined />}
                  onClick={(e) => { e.stopPropagation(); setEditandoPauta(true); }} />
              : undefined,
            children: editandoPauta ? (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <RichTextEditor
                  value={pautaHtml}
                  onChange={(v: string) => {
                    setPautaHtml(v); pautaDirtyRef.current = true; setPautaDirty(true);
                  }}
                />
                <Space>
                  <Button type="primary" icon={<SaveOutlined />} loading={salvandoPauta}
                    onClick={salvarPauta}>Salvar pauta</Button>
                  <Button onClick={() => {
                    setPautaHtml(pauta.pauta_html || '');
                    pautaDirtyRef.current = false; setPautaDirty(false); setEditandoPauta(false);
                  }}>Cancelar</Button>
                </Space>
              </Space>
            ) : (
              pauta.pauta_html
                ? <div className="conteudo-html"
                    dangerouslySetInnerHTML={{ __html: pauta.pauta_html }} />
                : <Text type="secondary">
                    A pauta ainda não foi montada.{podeGerenciar ? ' Use "Montar de novo".' : ''}
                  </Text>
            ),
          },
          {
            key: 'combinados',
            label: (
              <Space>
                <Text strong>Combinados de hoje</Text>
                {num(pauta.n_encaminhamentos) > 0 && (
                  <Tag>{num(pauta.n_convertidos)}/{num(pauta.n_encaminhamentos)} viraram tarefa</Tag>
                )}
              </Space>
            ),
            children: (
              <CombinadosPanel reuniaoId={id} combinados={combinados}
                podeGerenciar={podeGerenciar}
                onMudou={async () => { await carregar(); onMudouLista(); }} />
            ),
          },
          {
            key: 'ata',
            label: (
              <Space>
                <Text strong>Ata</Text>
                {ataDirty && <Tag color="orange">não salva</Tag>}
                {!ataDirty && pauta.tem_ata && <Tag color="green">escrita</Tag>}
              </Space>
            ),
            children: podeGerenciar ? (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <RichTextEditor
                  value={ataHtml}
                  onChange={(v: string) => {
                    setAtaHtml(v); ataDirtyRef.current = true; setAtaDirty(true);
                  }}
                />
                <Button type="primary" icon={<SaveOutlined />} loading={salvandoAta}
                  disabled={!ataDirty} onClick={salvarAta}>Salvar ata</Button>
              </Space>
            ) : (
              pauta.ata_html
                ? <div className="conteudo-html"
                    dangerouslySetInnerHTML={{ __html: pauta.ata_html }} />
                : <Text type="secondary">A ata desta reunião ainda não foi escrita.</Text>
            ),
          },
          {
            key: 'material',
            label: (
              <Space>
                <Text strong>Material técnico</Text>
                {num(pauta.n_anexos) > 0 && <Tag>{num(pauta.n_anexos)}</Tag>}
              </Space>
            ),
            children: (
              <MaterialPanel reuniaoId={id} anexos={anexos} podeGerenciar={podeGerenciar}
                onMudou={async () => { await carregar(); onMudouLista(); }} />
            ),
          },
          {
            key: 'rodizio',
            label: <Text strong>Rodízio de apresentador</Text>,
            children: <EscalaPanel podeGerenciar={podeGerenciar}
              semanaAtual={isoDia(pauta.semana_ref)} />,
          },
        ]}
      />
    </Space>
  );
}
